import { useEffect, useState } from "react";
import { askGuiaIa } from "@/services/guiaIa";
import { logShare } from "@/services/affiliate";
import { webinarLink } from "@/services/webinar";
import BulkSender from "./BulkSender";
import { buildMessage } from "./shared";

const PURPLE = "#7C3AED";
const DRAFT_KEY = "vv-wb-openers";

const RELATIONS = ["Amigo/a", "Familiar", "Compañero/a de trabajo", "Conocido/a", "Cliente", "Contacto nuevo"];
const TONES = ["Cercano y cálido", "Breve y directo", "Respetuoso / formal", "Con humor suave"];
const QUICK = ["Más corto", "Más cálido", "Más formal", "Más curioso"];
const DEFAULT_OPENER = "Hola {nombre} 😊 Estuve viendo un webinar corto y gratuito de Vívelo AI sobre cómo desarrollar tu potencial con propósito y pensé en ti. ¿Te gustaría verlo cuando tengas 15 minutos?";

function clean(text: string): string {
  return text.replace(/^["“”«»\s]+|["“”«»\s]+$/g, "").trim();
}

const RULES =
  "Reglas: máximo 60 palabras; escribe {nombre} donde iría el nombre de la persona; NO incluyas ningún enlace (yo lo agrego); termina con una pregunta suave; sin presión, sin promesas de ingresos ni resultados garantizados; natural y humano, no de plantilla.";

export default function MessageStudio({ code }: { code: string | null }) {
  const [templates, setTemplates] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      const arr = raw ? (JSON.parse(raw) as string[]) : null;
      if (Array.isArray(arr) && arr.length) return arr.slice(0, 3);
    } catch {
      // se usa el mensaje base
    }
    return [DEFAULT_OPENER];
  });
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [cName, setCName] = useState("");
  const [relation, setRelation] = useState(RELATIONS[0]);
  const [about, setAbout] = useState("");
  const [tone, setTone] = useState(TONES[0]);
  const [refine, setRefine] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const link = code ? webinarLink(code, "whatsapp") : "";
  const tpl = templates[idx] ?? "";

  useEffect(() => {
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(templates));
    } catch {
      // nada
    }
  }, [templates]);

  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2200);
  };

  const setTpl = (v: string) => setTemplates((arr) => arr.map((t, i) => (i === idx ? v : t)));

  const ask = async (prompt: string, history?: { role: "user" | "assistant"; content: string }[]) => {
    setBusy(true);
    setErr(null);
    const r = await askGuiaIa([...(history ?? []), { role: "user", content: prompt }]);
    setBusy(false);
    if (r.ok === true) return r.reply;
    setErr((r as { message: string }).message);
    return null;
  };

  const context = () =>
    `Datos del contacto: ${cName.trim() ? `nombre ${cName.trim()}; ` : ""}relación: ${relation}${about.trim() ? `; contexto o intereses: ${about.trim()}` : ""}. Tono: ${tone}.`;

  const base = `Redacta un mensaje de apertura (opener) para enviar por WhatsApp invitando a una persona a ver un webinar gratuito de Vívelo AI.`;

  const generate = async (count: 1 | 3) => {
    const sep = count === 3 ? " Escribe 3 versiones distintas entre sí, separadas por una línea que contenga solo ---, sin numerarlas." : " Entrega solo el mensaje.";
    const reply = await ask(`${base} ${context()} ${RULES}${sep}`);
    if (!reply) return;
    const parts = reply.split(/\n\s*-{3,}\s*\n/).map(clean).filter(Boolean).slice(0, 3);
    if (!parts.length) return setErr("No se pudo leer la respuesta. Inténtalo de nuevo.");
    setTemplates(parts);
    setIdx(0);
  };

  const refineNow = async (instruction: string) => {
    if (!instruction.trim() || !tpl.trim()) return;
    const reply = await ask(`Modifica el mensaje anterior así: ${instruction.trim()}. ${RULES} Entrega solo el mensaje nuevo.`, [
      { role: "user", content: `${base} ${context()}` },
      { role: "assistant", content: tpl },
    ]);
    if (!reply) return;
    setTpl(clean(reply));
    setRefine("");
  };

  const preview = buildMessage(tpl, cName, link || "https://app.vivelo-ai.com/webinar");

  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(preview);
      flash("Mensaje copiado");
    } catch {
      flash("No se pudo copiar.");
    }
  };

  const sendOne = () => {
    if (!code) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(preview)}`, "_blank", "noopener");
    void logShare("whatsapp");
  };

  const fieldCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-violet-400";

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5">
      <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2">
        <i className="ri-magic-line" style={{ color: PURPLE }}></i> Mensaje de apertura con la Guía IA
      </h2>
      <p className="text-xs text-slate-500 mt-0.5">Escribe tu mensaje, pídele uno a la Guía o ajústalo. Tu link se agrega solo al final.</p>

      {templates.length > 1 ? (
        <div className="mt-3 flex gap-2" role="tablist" aria-label="Versiones">
          {templates.map((_, i) => (
            <button key={i} role="tab" aria-selected={idx === i} onClick={() => setIdx(i)} className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap ${idx === i ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}>
              Versión {i + 1}
            </button>
          ))}
        </div>
      ) : null}

      <textarea value={tpl} onChange={(e) => setTpl(e.target.value)} rows={4} maxLength={600} className={`${fieldCls} mt-3 resize-y`} aria-label="Mensaje de apertura" />
      <p className="text-[11px] text-slate-400 mt-1">Usa {"{nombre}"} para que se reemplace por el nombre de cada contacto.</p>

      <details className="mt-3 group rounded-xl border border-slate-200 bg-slate-50" open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary className="cursor-pointer select-none px-3 py-2.5 text-sm font-semibold text-slate-800 flex items-center justify-between list-none">
          <span><i className="ri-user-heart-line mr-1.5" style={{ color: PURPLE }}></i>Cuéntale a la Guía cómo es tu contacto</span>
          <i className="ri-arrow-down-s-line text-lg transition-transform group-open:rotate-180"></i>
        </summary>
        <div className="px-3 pb-3 grid gap-2.5 sm:grid-cols-2">
          <input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="Nombre (opcional)" maxLength={60} className={fieldCls} aria-label="Nombre del contacto" />
          <select value={relation} onChange={(e) => setRelation(e.target.value)} className={fieldCls} aria-label="Relación">
            {RELATIONS.map((r) => <option key={r}>{r}</option>)}
          </select>
          <textarea value={about} onChange={(e) => setAbout(e.target.value)} rows={2} maxLength={300} placeholder="Cómo es: a qué se dedica, qué le interesa, qué momento vive…" className={`${fieldCls} sm:col-span-2 resize-none`} aria-label="Cómo es el contacto" />
          <select value={tone} onChange={(e) => setTone(e.target.value)} className={fieldCls} aria-label="Tono">
            {TONES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <div className="flex gap-2">
            <button onClick={() => generate(1)} disabled={busy} className="flex-1 px-3 py-2 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-60" style={{ background: PURPLE }}>
              {busy ? "Escribiendo…" : "Generar mensaje"}
            </button>
            <button onClick={() => generate(3)} disabled={busy} className="px-3 py-2 rounded-lg border border-violet-300 text-violet-700 text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-60">
              3 versiones
            </button>
          </div>
        </div>
      </details>

      <div className="mt-3">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {QUICK.map((q) => (
            <button key={q} onClick={() => refineNow(q)} disabled={busy} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-60">
              {q}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={refine} onChange={(e) => setRefine(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refineNow(refine)} placeholder="Pídele un cambio: «menciona que es gratis», «hazlo para alguien joven»…" maxLength={200} className={fieldCls} aria-label="Pedir un cambio" />
          <button onClick={() => refineNow(refine)} disabled={busy || !refine.trim()} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50">
            Aplicar
          </button>
        </div>
      </div>
      {err ? <p className="mt-2 text-xs text-rose-600">{err}</p> : null}

      <div className="mt-4 rounded-xl bg-[#E7FFDB] border border-emerald-200 p-3 text-sm text-slate-800 whitespace-pre-wrap break-words">{preview}</div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={sendOne} disabled={!code} className="px-4 py-2.5 rounded-lg bg-[#25D366] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50">
          <i className="ri-whatsapp-fill mr-1.5"></i>Enviar a un contacto
        </button>
        <button onClick={copyMsg} className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold cursor-pointer whitespace-nowrap">
          <i className="ri-file-copy-line mr-1.5"></i>Copiar mensaje
        </button>
      </div>

      <BulkSender code={code} templates={templates} tpl={tpl} />

      {toast ? (
        <div role="status" className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-[#0B1F3A] text-white text-sm shadow-lg">{toast}</div>
      ) : null}
    </section>
  );
}
